import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  DomainIcon,
  ExchangeAltIcon,
  ExclamationCircleIcon,
  FileInvoiceDollarIcon,
  PlusCircleIcon,
  SearchIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../../api';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDomainNames } from '../../../hooks/useDomainNames';
import { useInFlightDomainRegistrations } from '../../../hooks/useInFlightDomainRegistrations';
import { formatDateNumeric } from '../../../utils/dateFormat';
import type { DomainName } from '../../../types/models';
import type { ApiResponseDomainBilling } from '../../../types/api/ApiResponseDomainName';
import ImportDomainModal from './ImportDomainModal';
import { AutoRenewLabel } from './DomainAutoRenewField';
import PendingRegistrationsAlert from './PendingRegistrationsAlert';
import { tradeIneligibilityReason } from './Trade/types';

function Domains() {
  const auth = useAuth();
  const user = useUserContext();
  const navigate = useNavigate();
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { domainNames, loading, error, refetch } = useDomainNames();
  // Paid registrations the registry hasn't confirmed yet are not in the domain list; surface them
  // above it. Best effort: silent when there are none or the read fails.
  const inFlightRegistrations = useInFlightDomainRegistrations();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // Per-row enrollment overrides applied on top of the fetched list, so a successful toggle
  // updates only that row's link instead of triggering a full-page refetch/spinner. Keyed by
  // domain id; the value is the new `next_billing_at` (null after unenroll, a date after enroll).
  const [billingOverrides, setBillingOverrides] = useState<Record<number, string | null>>({});
  const [billingBusyIds, setBillingBusyIds] = useState<Set<number>>(new Set());

  // The auto-invoicing column is an Ancalagon-staff-only action. The backend gates enroll/unenroll
  // on the *real* user (never the impersonated tenant), and staff impersonate the target client to
  // manage its domains — so this is gated purely on the staff flag, not on impersonation state.
  const isStaff = user?.is_ancalagon_staff === true;
  // A backend without the auto-renew feature omits `auto_renew`; hide the column rather than show
  // a column of dashes a customer could read as "not renewing".
  const hasAutoRenew = domainNames.some((d) => Boolean(d.auto_renew));

  const handleImportClose = () => {
    setIsImportOpen(false);
    refetch();
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Drop the optimistic per-row enrollment overrides whenever a fresh list arrives (the hook only
  // re-assigns `domainNames` on a completed fetch, never on a toggle), so freshly-fetched
  // `next_billing_at` values are authoritative and never masked by a stale override.
  useEffect(() => {
    setBillingOverrides({});
  }, [domainNames]);

  const filteredDomains = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return domainNames;
    return domainNames.filter((d) => {
      const contact = d.registrant_contact;
      // Join name + registrant fields so a query like "ada lovelace" matches across first/last name.
      const haystack = [d.name, contact?.first_name, contact?.last_name, contact?.organization]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [domainNames, debouncedSearch]);

  const paginatedDomains = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredDomains.slice(start, start + perPage);
  }, [filteredDomains, page, perPage]);

  const handleToggleBilling = useCallback(
    async (domain: DomainName, currentlyEnrolled: boolean) => {
      if (domain.id === null) {
        return;
      }
      const id = domain.id;

      setBillingBusyIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      try {
        const response = await apiFetch(`/datacenters/domain-names/${id}/billing/enroll`, {
          method: currentlyEnrolled ? 'DELETE' : 'POST',
        });
        // JSend: the backend maps controller exceptions to status:'error' at HTTP 200, so the
        // authoritative check is body.status, not response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseDomainBilling | null;

        if (body?.status !== 'success') {
          throw new Error(body?.message ?? 'The request failed. Please try again.');
        }

        const nextBillingAt = body.data?.next_billing_at ?? null;
        setBillingOverrides((prev) => ({ ...prev, [id]: nextBillingAt }));

        addNotification({
          title: currentlyEnrolled ? 'Auto invoicing removed' : 'Auto invoicing enabled',
          srTitle: 'Success alert',
          description: currentlyEnrolled
            ? `${domain.name} is no longer enrolled in automatic renewal invoicing.`
            : `${domain.name} is enrolled in automatic renewal invoicing${
                nextBillingAt ? ` — next charge ${formatDateNumeric(nextBillingAt)}` : ''
              }.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not update auto invoicing',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setBillingBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [apiFetch, addNotification, setDrawerOpen],
  );

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const rowKey = (d: DomainName) => (d.id !== null ? d.id : d.name);

  // Per-row entry to the internal-trade wizard (change the registrant of a .be domain already
  // registered here). Ineligible rows keep the item visible but isAriaDisabled + tooltip (not
  // isDisabled — a hard-disabled menu item isn't hoverable/focusable, so its reason never shows).
  const rowActions = (d: DomainName): IAction[] => {
    const tradeReason = tradeIneligibilityReason(d);
    return [
      {
        title: 'Change owner',
        isAriaDisabled: tradeReason !== null,
        ...(tradeReason ? { tooltipProps: { content: tradeReason } } : {}),
        onClick: () => navigate(`/datacenter/domains/trade?domain=${encodeURIComponent(d.name)}`),
      },
    ];
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load domain names"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (domainNames.length === 0) {
      return (
        <EmptyState titleText="No domain names yet" icon={DomainIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            You don&apos;t have any domain names yet. Register a new domain to get started, or import an existing one.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                component={(props) => <Link {...props} to="/datacenter/domains/register" />}
              >
                Register a new domain
              </Button>
              <Button
                variant="secondary"
                icon={<ExchangeAltIcon />}
                component={(props) => <Link {...props} to="/datacenter/domains/transfer" />}
              >
                Transfer a domain
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by name, owner, or organization..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredDomains.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredDomains.length === 0 ? (
          <EmptyState titleText="No domain names match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all domain names.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setSearchValue('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Domain names list" variant="compact">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>First name</Th>
                <Th>Last name</Th>
                <Th>Organization</Th>
                <Th>Registrar</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                {hasAutoRenew && <Th>Auto-renew</Th>}
                {isStaff && <Th>Auto invoicing</Th>}
                <Th screenReaderText="Row actions" />
              </Tr>
            </Thead>
            <Tbody>
              {paginatedDomains.map((d) => {
                const enrolledAt =
                  d.id !== null && d.id in billingOverrides ? billingOverrides[d.id] : d.next_billing_at;
                const isEnrolled = enrolledAt !== null;
                const isBillingBusy = d.id !== null && billingBusyIds.has(d.id);
                return (
                  <Tr key={rowKey(d)}>
                    <Td dataLabel="Name">
                      <Flex
                        spaceItems={{ default: 'spaceItemsSm' }}
                        alignItems={{ default: 'alignItemsCenter' }}
                        flexWrap={{ default: 'nowrap' }}
                      >
                        <FlexItem>
                          {d.id === null ? d.name : <Link to={`/datacenter/domains/${d.id}`}>{d.name}</Link>}
                        </FlexItem>
                        {d.in_transfer && (
                          <FlexItem>
                            <Label color="orange" isCompact>
                              In transfer
                            </Label>
                          </FlexItem>
                        )}
                      </Flex>
                    </Td>
                    <Td dataLabel="First name">{d.registrant_contact?.first_name || '—'}</Td>
                    <Td dataLabel="Last name">{d.registrant_contact?.last_name || '—'}</Td>
                    <Td dataLabel="Organization">{d.registrant_contact?.organization || '—'}</Td>
                    <Td dataLabel="Registrar">
                      {d.domain_registration_platform === null ? (
                        '—'
                      ) : (
                        <Label>{d.domain_registration_platform.name}</Label>
                      )}
                    </Td>
                    <Td dataLabel="Created">{formatDateNumeric(d.created_at)}</Td>
                    <Td dataLabel="Expires">{formatDateNumeric(d.expires_at)}</Td>
                    {hasAutoRenew && (
                      <Td dataLabel="Auto-renew">{d.auto_renew ? <AutoRenewLabel autoRenew={d.auto_renew} /> : '—'}</Td>
                    )}
                    {isStaff && (
                      <Td dataLabel="Auto invoicing">
                        {d.id === null ? (
                          '—'
                        ) : (
                          <Button
                            variant="link"
                            isInline
                            icon={<FileInvoiceDollarIcon />}
                            isLoading={isBillingBusy}
                            isDisabled={isBillingBusy}
                            spinnerAriaLabel="Updating auto invoicing"
                            onClick={() => handleToggleBilling(d, isEnrolled)}
                            aria-label={
                              isEnrolled ? `Remove auto invoicing for ${d.name}` : `Enroll auto invoicing for ${d.name}`
                            }
                          >
                            {isEnrolled ? 'Remove auto invoicing' : 'Enroll auto invoicing'}
                          </Button>
                        )}
                      </Td>
                    )}
                    <Td isActionCell>
                      <ActionsColumn items={rowActions(d)} />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {filteredDomains.length > 0 && (
          <Pagination
            itemCount={filteredDomains.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Domain Names</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <DomainIcon />
              </Icon>
              Domain Names
            </Content>
            <Content component="p">Manage your registered domain names</Content>
          </FlexItem>
          {!loading && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                <FlexItem>
                  <Button
                    variant="primary"
                    icon={<PlusCircleIcon />}
                    component={(props) => <Link {...props} to="/datacenter/domains/register" />}
                  >
                    Register a new domain
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="secondary"
                    icon={<ExchangeAltIcon />}
                    component={(props) => <Link {...props} to="/datacenter/domains/transfer" />}
                  >
                    Transfer a domain
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button variant="secondary" icon={<PlusCircleIcon />} onClick={() => setIsImportOpen(true)}>
                    Import Domain
                  </Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        {/* Outside renderContent so it also shows over the "no domain names yet" empty state —
            the first registration of a new customer is exactly the case it is for. */}
        <PendingRegistrationsAlert registrations={inFlightRegistrations} className="pf-v6-u-mb-md" />
        {renderContent()}
      </PageSection>
      <ImportDomainModal isOpen={isImportOpen} onClose={handleImportClose} />
    </>
  );
}

export { Domains };
